/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { KButton, KDialog, KIcon, KMenuDropDown } from "@kyburz/kdesign-system";
import { useHistory } from "react-router";
import useSetState from "../../hooks/useSetState";
import { setAlert } from "../../redux/actions/actions";
import { getFirmwares, deleteFirmware } from "../../apiCalls/config/firmware";
import { formatDate } from "../../utils/timeUtils";
import KInfiniteTable from "../../components/table/KInfiniteTable";
import { FirmwareType, fwTypeOptionFor } from "./typings";
import { useStyles } from "./styles";

const MemoizedKInfiniteTable = React.memo(
  (props: any) => (
    <KInfiniteTable
      data={props.data}
      hasNextPage={props.hasNextPage}
      isNextPageLoading={props.isNextPageLoading}
      loadNextPage={props.loadNextPage}
      columns={props.columns}
      updateMyData={props.updateMyData}
      hasStickyColumns={true}
      filterTable={props.filterTable}
      sortTable={props.sortTable}
      hasFilter
      manualFilters
      manualSortBy
    />
  ),
  (oldProps, newProps) => {
    return oldProps.data === newProps.data;
  },
);

export const defaultFirmware: FirmwareType = {
  hardwareType: "",
  firmwareType: "normal",
  tags: [],
  description: "",
  id: "",
  version: "",
  createdAt: "",
  contentsBase64: null,
  allowed_customers: [],
};

const Firmware: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const firstUpdate = React.useRef(true);
  const { CancelToken } = axios;

  const [
    {
      query,
      pagesLoaded,
      hasNextPage,
      isNextPageLoading,
      items,
      deleteModalOpen,
      reload,
      cancelTokenSource,
      filterBy,
      sortBy,
    },
    setState,
  ] = useSetState({
    query: "",
    pagesLoaded: 0,
    hasNextPage: true,
    isNextPageLoading: false,
    items: [],
    deleteModalOpen: false,
    reload: false,
    cancelTokenSource: null,
    filterBy: "",
    sortBy: "",
  });

  const [firmwareToDelete, setDeleteFirmwareState] =
    useSetState(defaultFirmware);

  const handleDelete = async () => {
    setState({ deleteModalOpen: false, query: "" });
    setDeleteFirmwareState(defaultFirmware);
    const res = await deleteFirmware(firmwareToDelete.id);
    if (res.data) {
      dispatch(
        setAlert(`Firmware ${firmwareToDelete.version} deleted`, "success"),
      );
      setState({ reload: true });
    } else {
      dispatch(setAlert(res.error, "error"));
    }
  };

  const toggleDeletePopup = () => {
    setState({ deleteModalOpen: !deleteModalOpen });
    setDeleteFirmwareState(defaultFirmware);
  };

  const navigateToCreatePage = () => {
    history.push("/config/devices/firmware/create/editor");
  };

  const loadFirmwarePage = (
    query,
    page,
    filterBy,
    sortBy,
    startEmpty = true,
  ) => {
    if (cancelTokenSource !== null) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();

    setState({ isNextPageLoading: true, cancelTokenSource: source });

    const request: any = getFirmwares(
      query,
      page,
      source.token,
      filterBy,
      sortBy,
    );

    request
      .then((res) => {
        const previousArray = startEmpty ? [] : items;
        setState({
          items: [...previousArray].concat(
            ...res.data?.data.map((x) => ({
              id: x.id,
              hardwareType: x.hardware_type,
              version: x.version,
              firmwareType: fwTypeOptionFor(x.firmware_type).value,
              created: formatDate(x.created_at),
              tags: x.tags || [],
              description: x.description,
              allowed_customers: x?.allowed_customers,
            })),
          ),
          isNextPageLoading: false,
          pagesLoaded: page,
          hasNextPage: res.data.has_next_page,
          cancelToken: null,
        });
      })
      .catch((e) => {
        setState({
          isNextPageLoading: false,
          pagesLoaded: page,
          cancelToken: null,
        });
        console.error(e);
      });
  };

  const loadNextPage = () => {
    loadFirmwarePage(query, pagesLoaded + 1, filterBy, sortBy, false);
  };

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      loadFirmwarePage(query, 1, filterBy, sortBy);
    }
  }, [reload]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    loadFirmwarePage(query, 1, filterBy, sortBy);
  }, [query, filterBy, sortBy]);

  const columns = React.useMemo(
    () => [
      {
        defaultCanSort: true,
        Header: "ID",
        disableFilters: true,
        accessor: "id",
        minWidth: 100,
        width: "30%",
      },
      {
        defaultCanSort: true,
        Header: "Name",
        accessor: "version",
        id: "version",
      },
      {
        defaultCanSort: true,
        Header: "FW Type",
        accessor: "firmwareType",
        id: "firmware_type",
        disableFilters: true,
      },
      {
        defaultCanSort: true,
        Header: "Hardware Type",
        accessor: "hardwareType",
        id: "hardware_type",
      },
      {
        Header: "Tags",
        defaultCanSort: false,
        id: "tags",
        accessor: (firmware) => firmware.tags.join(", "),
      },
      {
        Header: "Description",
        accessor: "description",
        id: "description",
        defaultCanSort: true,
      },
      {
        Header: "Companies",
        id: "customer",
        defaultCanSort: false,
        accessor: (firmware) => firmware?.allowed_customers?.join(", "),
      },
      {
        defaultCanSort: true,
        disableFilters: true,
        Header: "Created",
        id: "created_at",
        accessor: "created",
      },
      {
        accessor: "actions",
        disableFilters: true,
        Cell: (row) => {
          return (
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "flex-end",
                marginRight: 14,
              }}
            >
              <KMenuDropDown
                menuItems={[
                  {
                    icon: "edit",
                    title: "Edit",
                    action: () => {
                      const id = row.row.original?.id;
                      history.push(`/config/devices/firmware/${id}/editor`);
                    },
                  },
                  {
                    icon: "trash-2",
                    title: "Delete",
                    action: () => {
                      setState({ deleteModalOpen: true });
                      setDeleteFirmwareState(row.row.original);
                    },
                  },
                ]}
              />
            </div>
          );
        },
        width: 50,
        sticky: "right",
      },
    ],
    [],
  );

  const filterTable = (values: { id: string; value: string }[]): void => {
    const transformIdToFilter = (value: string): string => {
      switch (value) {
        case "hardwareType":
          return "filter_hardware_type";
        default:
          return `filter_${value}`;
      }
    };
    const parsedFilter: string = values
      .map((x) => `${transformIdToFilter(x.id)}=${x.value}`)
      .join("&");
    setState({ filterBy: parsedFilter });
  };

  const sortTable = (values: { id: string; desc: boolean }[]): void => {
    const transformIdToSort = (value: string, desc: boolean): string => {
      const order = desc ? "DESC" : "ASC";
      switch (value) {
        case "hardware":
          return `hardware_type ${order}`;
        default:
          return `${value} ${order}`;
      }
    };
    const parsedSort: string = values
      .map((x) => `${transformIdToSort(x.id, x.desc)}`)
      .join(",");
    setState({ sortBy: parsedSort });
  };

  const deletePopup = (
    <KDialog
      size="xs"
      title="Are you sure?"
      open={deleteModalOpen}
      primaryCTAonClick={handleDelete}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => toggleDeletePopup()}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want to delete the firmware{" "}
        <b>{firmwareToDelete.version}</b>?
      </p>
    </KDialog>
  );

  return (
    <>
      <div className={classes.ctaContainer}>
        <div style={{ flexGrow: 1 }} />
        <KButton
          children="Add Firmware"
          icon={<KIcon icon="plus" />}
          onClick={navigateToCreatePage}
          style={{ marginLeft: "auto" }}
        />
      </div>
      <div className={classes.tableContainer}>
        <MemoizedKInfiniteTable
          loadNextPage={loadNextPage}
          isNextPageLoading={isNextPageLoading}
          columns={columns}
          data={items}
          hasNextPage={hasNextPage}
          filterTable={filterTable}
          sortTable={sortTable}
        />
      </div>
      {deletePopup}
    </>
  );
};

export default Firmware;
